import type { CustomerType, RealEstateContact } from "../../../../@types/sharedSchema";
import { contactIdForPhone, getPhoneCandidates } from "./sellerPhone";
import type { ListingSellerDraft } from "./ListingSellerSection";
import { isContactDuplicateError, isContactNotInYourBookError } from "utils/contacts/contactWriteErrors";
import { readContactOnce } from "utils/contacts/readContactDocument";
import type { ContactGetter } from "utils/contacts/readContactDocument";
import type { ContactWrites } from "hooks/contacts/useContactWrites";

/**
 * Turn a listing's typed seller into a real, linked CRM contact.
 *
 * Runs AFTER the listing exists, because both links need its id. The order is
 * fixed and the reason is the failure mode:
 *
 *   1. resolve-or-create the contact          (needs the phone)
 *   2. contact.sellingListingIds += listingId (needs the listing id)
 *   3. listing.listingContacts[0].contactId   (needs the contact id)
 *
 * A failure between 2 and 3 leaves the CONTACT pointing at the listing but not
 * the reverse. That is the better way round: the listing surfaces read step 3
 * and degrade to showing the typed name and phone — which is exactly what the
 * realtor entered — while the CRM's seller lens, which reads step 2, is already
 * correct. The opposite order would show a linked contact that the CRM does not
 * know about.
 *
 * Nothing here throws into the caller's save path. The listing is already
 * written by this point, and losing a link is not worth losing the listing.
 */

/**
 * WHICH STORE THE `listingId` NAMES. Step 3 only exists for one of them.
 *
 * The server's link endpoint resolves the id against `real-estate-listings`
 * ONLY (`loadOwnedListing`), so a `yad2-listings-latest` id — which is what every
 * board surface holds — can only ever 404. Before this discriminator existed the
 * badge passed exactly that id, the 404 was swallowed to `console.error`, and the
 * save HALF-FAILED silently while the UI promised "מקושר לנכס".
 *
 *  - `realEstateListing` — the caller's own listing document. Step 3 runs.
 *  - `marketAd`          — a `yad2-listings-latest` row that is NOT a claimed
 *                          copy. Step 3 is impossible and is deliberately not
 *                          attempted; the result reports `linkSkipped`.
 *  - `unknown`           — the caller could not say. Step 3 is attempted and a
 *                          failure is treated as expected rather than as an
 *                          error. Callers should stop using this.
 *
 * NOTE the one non-obvious case: a webot MIRROR row's document id IS its
 * `real-estate-listings` id (the Cloud Function writes `yad2-listings-latest/
 * ${listingId}`), so a board hit with `listingOrigin === 'webot'` is
 * `realEstateListing`, not `marketAd`.
 */
export type ListingIdKind = "realEstateListing" | "marketAd" | "unknown";

/**
 * The lead source stamped on a contact created from a listing's seller.
 *
 * ONE value for every call site on purpose. The badge used to pass
 * `t("listingsBoard.claim.contactOrigin")` — a TRANSLATED string, so the same
 * action stamped "לוח נכסים" for a Hebrew user and "Listings board" for an
 * English one — while the two `NewListingDialog` call sites passed nothing at all
 * and fell through to `הוזן ידנית`. Three spellings for one action makes the
 * field uncountable, which is the only thing a lead source is for.
 *
 * It is a plain constant rather than a locale key because it is DATA on the
 * contact document, read back by the CRM's origin picker and by the server —
 * never re-rendered through i18n.
 */
export const LISTING_SELLER_CONTACT_ORIGIN = "לוח נכסים";

export type PersistListingSellerArgs = {
  listingId: string;
  /**
   * Which store `listingId` names — see `ListingIdKind`. Optional only so that
   * existing call sites keep compiling; every caller SHOULD pass it, and a caller
   * that cannot is telling us it does not know whether the link can work.
   */
  listingIdKind?: ListingIdKind;
  seller: ListingSellerDraft;
  /**
   * The contact document id when the caller ALREADY has one — the contact
   * `EditContactDialog` just created, or one the realtor picked.
   *
   * When set, this is authoritative and no phone lookup is used to choose the id:
   * the dialog has already written that document, and re-deriving an id from the
   * phone we happened to seed the dialog with is precisely how a second contact
   * got created under a Yad2 mask.
   */
  knownContactId?: string;
  /**
   * The phone the caller SEEDED its dialog with, when the realtor then changed it.
   *
   * Used only to detect a stale contact left behind by an earlier run under the
   * masked number, so the result can report it. Never written to — see
   * `findSupersededContact`.
   */
  supersedesPhone?: string;
  /**
   * May this call CREATE a contact that does not exist yet?
   *
   * Defaults to `true`, which is the right answer for every caller whose whole
   * purpose is to add the owner to the book (the board's save-owner badge). The
   * listing FORM passes the realtor's explicit choice instead: recording the owner
   * on the property and adding a person to the CRM are different acts, and a
   * qualification call routinely wants the first without the second.
   *
   * ⚠ IT NEVER GATES LINKING. When the phone already belongs to a contact, that
   * contact gets the listing link and the customer type regardless — that is not a
   * creation, and refusing it would leave the CRM ignorant of a relationship the
   * realtor just recorded on the listing.
   */
  createContact?: boolean;
  /** The listing's own type — a rental's owner is a משכיר, not a מוכר. */
  transactionType?: "sale" | "rent" | null;
  /**
   * Lead source for a contact this call CREATES. Defaults to
   * `LISTING_SELLER_CONTACT_ORIGIN`, so the origin is always supplied and always
   * the same string, whatever the call site and whatever the UI language.
   */
  contactOrigin?: string;
  /** The market ad's URL when the listing was claimed from one — stored as origin detail. */
  contactOriginAdditionalInfo?: string;
  userEmail: string;
  /**
   * `useWebotServer().contactsGet` — how the contact is READ: from where this browser reads
   * contacts (`readContactOnce`), so that in a shared office the decision and the merge patch
   * are made against the office's record, never the member's frozen copy from before the merge.
   */
  contactsGet: ContactGetter;
  /**
   * `useContactWrites()` — the two server writes this needs. An existing contact is EDITED
   * (`/contacts/update`, which refuses to create) and a new one is CREATED
   * (`/contacts/create`, which refuses a number that is taken), so the write can only do
   * what the read below decided it should.
   */
  writes: Pick<ContactWrites, "updateContact" | "createContact">;
  /** `WebotServerContext.linkListingContact`. */
  linkListingContact: (listingId: string, contactId: string | null, contactIndex?: number) => Promise<unknown>;
};

export type PersistListingSellerResult = {
  contactId: string | null;
  created: boolean;
  /** Step 3 succeeded: the listing document now points back at the contact. */
  linked: boolean;
  /**
   * Step 3 was deliberately NOT attempted because `listingId` names a market ad.
   * `linked: false, linkSkipped: true` is a SUCCESS — distinguish it from
   * `linked: false, linkSkipped: false`, which is a failure worth reporting.
   */
  linkSkipped: boolean;
  /**
   * A contact that still exists under the phone the caller seeded with, left by an
   * earlier run before the realtor corrected the number. Reported, never edited.
   */
  supersededContactId?: string;
};

const NO_RESULT: PersistListingSellerResult = {
  contactId: null,
  created: false,
  linked: false,
  linkSkipped: false
};

/**
 * Finds the caller's existing contact for this phone, across every spelling a
 * document id might use. Returns null when there is none.
 *
 * A one-shot read (`readContactOnce`), not the live hook: this runs once, at save
 * time, and the answer decides between creating and merging. The hook exists for the UI.
 */
const findExistingContact = async (
  contactsGet: ContactGetter,
  userEmail: string,
  phone: string
): Promise<RealEstateContact | null> => {
  const candidates = getPhoneCandidates(phone);
  if (!candidates.length) return null;
  return readContactOnce(contactsGet, userEmail, { candidates });
};

/**
 * Reads one contact by document id — the precise form of the lookup above, used
 * when the caller already knows which document it means.
 *
 * It must be a direct read rather than the candidate query: the two can disagree
 * (a legacy document under another spelling of the same number), and writing the
 * merge patch computed from one document onto a different one is how a contact
 * loses its `customerTypes`.
 */
const getContactById = (
  contactsGet: ContactGetter,
  userEmail: string,
  contactId: string
): Promise<RealEstateContact | null> => readContactOnce(contactsGet, userEmail, { contactId });

/**
 * The contact an earlier run created under the MASKED number, if it is still
 * there and is not the contact we are now linking.
 *
 * ## Why this only REPORTS
 *
 * The old badge threw away the contact the dialog created and re-derived
 * everything from the scraped ad, so correcting a Yad2 mask — the one thing the
 * UI told the realtor to do — produced a second contact under the mask, holding
 * `sellingListingIds: [thisListing]`. Those stale back-links are real, and it is
 * tempting to repair them here by rewriting that document's array.
 *
 * We deliberately do not. When this was written the only contact write available
 * here was the browser's `set(merge: true)`, which resolved the prior state from the
 * in-browser `allContacts` array and took its CREATE branch whenever the document was
 * not in it — a repair aimed at tidying a stale link could corrupt the very contact it
 * was tidying. The writes below go through the server now, so that trap is gone; what
 * remains true is that two cards for one person is a MERGE, and which card survives
 * is the realtor's decision. Reporting the id lets a caller offer it in the UI.
 *
 * Going forward the orphan cannot be created at all: `knownContactId` means the
 * masked phone is never used to mint a document again.
 */
const findSupersededContact = async (
  contactsGet: ContactGetter,
  userEmail: string,
  supersedesPhone: string | undefined,
  contactId: string
): Promise<string | undefined> => {
  const seeded = contactIdForPhone(supersedesPhone ?? "");
  if (!seeded || seeded === contactId) return undefined;
  try {
    const stale = await findExistingContact(contactsGet, userEmail, supersedesPhone as string);
    return stale?.id && stale.id !== contactId ? stale.id : undefined;
  } catch (error) {
    console.error("Failed to check for a superseded seller contact:", error);
    return undefined;
  }
};

export async function persistListingSeller({
  listingId,
  listingIdKind = "unknown",
  seller,
  knownContactId,
  supersedesPhone,
  createContact = true,
  transactionType,
  contactOrigin,
  contactOriginAdditionalInfo,
  userEmail,
  contactsGet,
  writes,
  linkListingContact
}: PersistListingSellerArgs): Promise<PersistListingSellerResult> {
  const phone = seller.phone?.trim();
  // No phone means no contact: the document id IS the phone. A named seller with
  // no number stays as the listing's typed entry, which is still useful.
  if ((!phone && !knownContactId) || !userEmail || !listingId) return NO_RESULT;

  const canonicalId = phone ? contactIdForPhone(phone) : null;
  if (!knownContactId && !canonicalId) return NO_RESULT;

  try {
    // An existing contact keeps ITS id, whatever spelling it was stored under.
    // Writing the canonical one instead would create a second document for the
    // same person — the duplicate this whole lookup exists to prevent.
    const readExisting = (): Promise<RealEstateContact | null> =>
      knownContactId
        ? getContactById(contactsGet, userEmail, knownContactId)
        : findExistingContact(contactsGet, userEmail, phone as string);

    let existing = await readExisting();

    // She recorded an owner but did not ask for a contact card. The seller is
    // already stored on the listing by the form's own save, so there is nothing
    // further to do — and minting a document she declined is exactly the silent
    // behaviour this switch was added to remove.
    //
    // Only reachable when nothing was found: an EXISTING contact is linked
    // regardless, because linking is not creating.
    if (!existing && !createContact) return NO_RESULT;

    const isRent = transactionType === "rent";
    const customerType: CustomerType = isRent ? "משכיר" : "מוכר";

    /** The listing's link for this contact as it stands — its own links kept. */
    const linkPatchFor = (contact: RealEstateContact | null): Partial<RealEstateContact> => {
      const currentLinks = (isRent ? contact?.rentingOutListingIds : contact?.sellingListingIds) ?? [];
      const nextLinks = currentLinks.includes(listingId) ? currentLinks : [...currentLinks, listingId];
      // Written as two explicit branches rather than a computed `[linkField]` key:
      // a computed key widens the object to `{[x: string]: …}`, which type-checks
      // against nothing and would let a typo silently become a new contact field.
      return isRent ? { rentingOutListingIds: nextLinks } : { sellingListingIds: nextLinks };
    };

    /**
     * An existing contact gets the link and the type ADDED — never its identity
     * overwritten. She may have spent months filling that card in, and the scraped
     * seller name is frequently an agency or a mangled string.
     */
    const addTo = async (contactId: string, contact: RealEstateContact): Promise<void> => {
      const existingTypes = contact.customerTypes ?? [];
      const nextTypes: CustomerType[] = existingTypes.includes(customerType)
        ? existingTypes
        : [...existingTypes, customerType];
      await writes.updateContact(contactId, { ...linkPatchFor(contact), customerTypes: nextTypes });
    };

    /** Writes the link for the state that was READ, and returns the id it landed under. */
    const write = async (found: RealEstateContact | null): Promise<{ contactId: string; created: boolean }> => {
      const targetId = knownContactId ?? found?.id ?? (canonicalId as string);

      if (found) {
        await addTo(targetId, found);
        return { contactId: targetId, created: false };
      }

      const firstName = seller.firstName?.trim();
      const lastName = seller.lastName?.trim();
      // No `callerPhoneNumber`: the document id IS the phone, and the server seeds the field.
      const created = await writes.createContact({
        contactId: targetId,
        fields: {
          ...(firstName ? { callerFirstName: firstName } : {}),
          ...(lastName ? { callerLastName: lastName } : {}),
          customerTypes: [customerType],
          // Always supplied, never language-dependent — see the constant's docs.
          contactOrigin: contactOrigin || LISTING_SELLER_CONTACT_ORIGIN,
          ...(contactOriginAdditionalInfo ? { contactOriginAdditionalInfo } : {}),
          ...linkPatchFor(null)
        }
      });

      if (created.alreadyInOffice) {
        // A SHARED OFFICE already had this person (on a colleague's list, so the read above,
        // which asks about the caller's own list, did not see them): nothing was created and
        // none of the fields above was written — the server put the caller on the record's
        // list. The link and the type go on the office's record now, added to what it holds.
        // Never written from nothing: a patch built on no document would replace the office's
        // links with this one listing's.
        const office =
          created.doc ?? (await getContactById(contactsGet, userEmail, created.contactId));
        if (office) await addTo(created.contactId, office);
        return { contactId: created.contactId, created: false };
      }
      return { contactId: created.contactId, created: true };
    };

    let contactId: string;
    let created: boolean;
    try {
      ({ contactId, created } = await write(existing));
    } catch (error) {
      // The book moved between the read and the write: the contact we were about to edit
      // was deleted, or the one we were about to create now exists (a call transcribed
      // meanwhile, another tab). The server refused instead of guessing, so read again and
      // do what is right for what is there NOW. Once — a second miss is a real failure.
      const bookMoved = existing ? isContactNotInYourBookError(error) : isContactDuplicateError(error);
      if (!bookMoved) throw error;
      existing = await readExisting();
      if (!existing && !createContact) return NO_RESULT;
      ({ contactId, created } = await write(existing));
    }

    let linked = false;
    let linkSkipped = false;
    if (listingIdKind === "marketAd") {
      // The server resolves this id against `real-estate-listings` only, so a
      // market-ad id can only 404. Not attempting it is the difference between a
      // save that half-fails silently and one that reports what it actually did —
      // the UI copy must promise "saved as a contact" here, without "מקושר לנכס".
      linkSkipped = true;
    } else {
      try {
        await linkListingContact(listingId, contactId, 0);
        linked = true;
      } catch (error) {
        // Step 3 only. The contact exists and already points at the listing, so
        // the CRM is correct and the listing still shows the typed seller.
        if (listingIdKind === "unknown") {
          // Expected whenever the id turns out to name a market ad. Warn rather
          // than error so a caller that simply did not declare its id kind does
          // not fill the console with failures that are not failures.
          console.warn(
            "Could not link the seller contact to listing",
            listingId,
            "- if this is a market-ad id, pass listingIdKind: 'marketAd'.",
            error
          );
        } else {
          console.error("Failed to stamp contactId on the listing's seller entry:", error);
        }
      }
    }

    const supersededContactId = await findSupersededContact(contactsGet, userEmail, supersedesPhone, contactId);

    return {
      contactId,
      created,
      linked,
      linkSkipped,
      ...(supersededContactId ? { supersededContactId } : {})
    };
  } catch (error) {
    console.error("Failed to persist the listing's seller as a contact:", error);
    return NO_RESULT;
  }
}
